// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  TrendingDownOutlined,
  TrendingUpOutlined,
} from '@/constants/hardfin-standard-icons';
import { z } from 'zod';

export const costAdjustmentTypeSchema = z.enum([
  'CAPITALIZATION',
  'IMPAIRMENT',
]);

export type CostAdjustmentType = z.infer<typeof costAdjustmentTypeSchema>;

export const costAdjustmentReasonSchema = z.enum([
  'BETTERMENT',
  'ADDITION',
  'LIFE_EXTENSION',
  'INSTALLATION',
  'DAMAGE',
  'OBSOLESCENCE',
  'MARKET_DECLINE',
  'REGULATORY',
  'OTHER',
]);

export type CostAdjustmentReason = z.infer<typeof costAdjustmentReasonSchema>;

export const COST_ADJUSTMENT_TYPE_LABELS: Record<CostAdjustmentType, string> = {
  CAPITALIZATION: 'Capitalization',
  IMPAIRMENT: 'Impairment',
};

const COST_ADJUSTMENT_TYPE_TOOLTIPS: Record<CostAdjustmentType, string> = {
  CAPITALIZATION: 'Record an increase to the asset’s capitalized cost basis',
  IMPAIRMENT: 'Record a decrease to the asset’s capitalized cost basis',
};

export const COST_ADJUSTMENT_REASON_LABELS: Record<
  CostAdjustmentReason,
  string
> = {
  BETTERMENT: 'Betterment',
  ADDITION: 'Addition',
  LIFE_EXTENSION: 'Life extension',
  INSTALLATION: 'Installation',
  DAMAGE: 'Damage',
  OBSOLESCENCE: 'Obsolescence',
  MARKET_DECLINE: 'Market decline',
  REGULATORY: 'Regulatory',
  OTHER: 'Other',
};

const COST_ADJUSTMENT_REASONS_BY_TYPE: Record<
  CostAdjustmentType,
  CostAdjustmentReason[]
> = {
  CAPITALIZATION: [
    'BETTERMENT',
    'ADDITION',
    'LIFE_EXTENSION',
    'INSTALLATION',
    'OTHER',
  ],
  IMPAIRMENT: [
    'DAMAGE',
    'OBSOLESCENCE',
    'MARKET_DECLINE',
    'REGULATORY',
    'OTHER',
  ],
};

export const COST_ADJUSTMENT_TYPE_OPTIONS = [
  {
    value: 'CAPITALIZATION' as const,
    label: COST_ADJUSTMENT_TYPE_LABELS.CAPITALIZATION,
    icon: <TrendingUpOutlined fontSize="small" />,
    tooltip: COST_ADJUSTMENT_TYPE_TOOLTIPS.CAPITALIZATION,
  },
  {
    value: 'IMPAIRMENT' as const,
    label: COST_ADJUSTMENT_TYPE_LABELS.IMPAIRMENT,
    icon: <TrendingDownOutlined fontSize="small" />,
    tooltip: COST_ADJUSTMENT_TYPE_TOOLTIPS.IMPAIRMENT,
  },
];

export function getCostAdjustmentReasonOptions(type: CostAdjustmentType) {
  return COST_ADJUSTMENT_REASONS_BY_TYPE[type].map((reason) => ({
    value: reason,
    label: COST_ADJUSTMENT_REASON_LABELS[reason],
  }));
}
